import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Table,
  Container,
  Button,
  Collapse,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BACKEND_BASE } from "./constant";

const Interests = () => {
  const [interestsData, setInterestsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [actionLoading, setActionLoading] = useState(new Set());

  const [sortConfig, setSortConfig] = useState({
    key: "updatedAt",
    direction: "desc",
  });

  const navigate = useNavigate();

  /* ================= Fetch Interests ================= */
  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const response = await axios.get(`${BACKEND_BASE}/getAllInterests`);

        const data = response.data?.interests || [];

        // Group by senderEmail
        const grouped = data.reduce((acc, interest) => {
          if (!acc[interest.senderEmail]) acc[interest.senderEmail] = [];
          acc[interest.senderEmail].push(interest);
          return acc;
        }, {});

        const groupedArray = Object.entries(grouped).map(
          ([senderEmail, results]) => {
            const sortedResults = [...results].sort(
              (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
            );

            return {
              senderEmail,
              results: sortedResults,
              updatedAt: sortedResults[0]?.updatedAt || null,
            };
          },
        );

        setInterestsData(groupedArray);
      } catch (err) {
        console.error("Error fetching interests:", err);
        setError("Failed to load interests.");
      } finally {
        setLoading(false);
      }
    };

    fetchInterests();
  }, []);

  /* ================= Sorting ================= */
  const sortedData = useMemo(() => {
    const sortable = [...interestsData];

    sortable.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "updatedAt") {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else if (sortConfig.key === "senderEmail") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      } else if (sortConfig.key === "results") {
        aVal = a.results.length;
        bVal = b.results.length;
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [interestsData, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ================= Admin Action ================= */
  const handleAction = useCallback(async (interestId, actionType) => {
    setActionLoading((prev) => {
      const copy = new Set(prev);
      copy.add(interestId);
      return copy;
    });

    try {
      await axios.post(`${BACKEND_BASE}/updateInterest`, {
        interestId,
        action: actionType,
      });

      // Optimistic UI update
      setInterestsData((prev) =>
        prev.map((user) => ({
          ...user,
          results: user.results.map((i) =>
            i._id === interestId ? { ...i, adminStatus: actionType } : i,
          ),
        })),
      );
    } catch (err) {
      console.error("Action failed:", err);
      alert("Failed to update interest.");
    } finally {
      setActionLoading((prev) => {
        const copy = new Set(prev);
        copy.delete(interestId);
        return copy;
      });
    }
  }, []);

  /* ================= Render ================= */
  return (
    <Container>
      <h1 className="my-4">Interests Analytics</h1>

      <Button
        onClick={() => navigate("/")}
        className="mb-3"
        style={{ backgroundColor: "rgb(72, 39, 110)" }}
      >
        Back
      </Button>

      {loading && <Spinner animation="border" variant="info" />}

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && sortedData.length === 0 && (
        <p>No interests found.</p>
      )}

      {!loading && !error && sortedData.length > 0 && (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th onClick={() => requestSort("senderEmail")}>Sender</th>
              <th onClick={() => requestSort("results")}>
                Number of Interests
              </th>
              <th onClick={() => requestSort("updatedAt")}>Last Updated</th>
              <th>Details / Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map(
              (user, index) =>
                user.results[0].adminStatus !== "REJECTED" && (
                  <React.Fragment key={user.senderEmail}>
                    <tr>
                      <td>{user.senderEmail}</td>
                      <td>{user.results.length}</td>
                      <td>
                        {user.updatedAt
                          ? new Date(user.updatedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            setOpenIndex(openIndex === index ? null : index)
                          }
                        >
                          {openIndex === index ? "Hide" : "Show"} Interests
                        </Button>
                      </td>
                    </tr>

                    <tr>
                      <td colSpan="4" style={{ padding: 0, border: "none" }}>
                        <Collapse in={openIndex === index}>
                          <div
                            style={{
                              padding: 10,
                              background: "#f8f9fa",
                            }}
                          >
                            <Table size="sm" striped bordered>
                              <thead>
                                <tr>
                                  <th>Receiver</th>
                                  <th>Status</th>
                                  <th>Score</th>
                                  <th>Explanation</th>
                                  <th>Admin Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {user.results
                                  .filter(
                                    (i) =>
                                      i.adminStatus !== "REJECTED" &&
                                      ((i.status === "INTERESTED" &&
                                        i.adminStatus === "PENDING") ||
                                        i.status === "CONNECT" ||
                                        i.status === "REJECTED"),
                                  )
                                  .map((interest) => (
                                    <tr key={interest._id}>
                                      <td>{interest.receiverEmail}</td>
                                      <td>{interest.status}</td>
                                      <td>
                                        {interest.matchScore
                                          ? (interest.matchScore * 100).toFixed(
                                              2,
                                            ) + "%"
                                          : "—"}
                                      </td>
                                      <td>
                                        {interest.matchExplanation || "—"}
                                      </td>
                                      <td>
                                        {interest.status === "INTERESTED" ? (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="success"
                                              disabled={actionLoading.has(
                                                interest._id,
                                              )}
                                              onClick={() =>
                                                handleAction(
                                                  interest._id,
                                                  "APPROVED",
                                                )
                                              }
                                            >
                                              {actionLoading.has(interest._id)
                                                ? "..."
                                                : "Approve"}
                                            </Button>{" "}
                                            <Button
                                              size="sm"
                                              variant="danger"
                                              disabled={actionLoading.has(
                                                interest._id,
                                              )}
                                              onClick={() =>
                                                handleAction(
                                                  interest._id,
                                                  "REJECTED",
                                                )
                                              }
                                            >
                                              {actionLoading.has(interest._id)
                                                ? "..."
                                                : "Reject"}
                                            </Button>
                                          </>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ),
            )}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default Interests;
